import { Link } from "react-router";
import type { DailyProgress, OfferRow } from "../../rewards/earnList";
import { CoinIcon } from "../WalletChips";

/**
 * Presentational pieces shared by the store's Tasks section, the in-store Tasks panel and the "Earn
 * coins" list (pure props; server-rendered in unit tests). The sponsorship disclosure is always part of
 * the list; every task shows its kind, its reward, and what Townsy can honestly check.
 */

export function SponsoredDisclosure({ text, compact = false }: { text: string; compact?: boolean }) {
  return (
    <p className={`notice notice-info small offer-disclosure${compact ? " compact" : ""}`} role="note" data-testid="sponsored-disclosure" style={{ margin: 0 }}>
      {text}
    </p>
  );
}

export function DailyProgressMeter({ progress }: { progress: DailyProgress }) {
  return (
    <div className="daily-progress" data-testid="daily-progress">
      <div className="row" style={{ justifyContent: "space-between", gap: 6 }}>
        <span className="small">
          <CoinIcon /> <strong data-testid="daily-progress-text">{progress.text}</strong>
        </span>
        <span className="small muted">{progress.remaining} left today</span>
      </div>
      <div
        className="meter"
        role="meter"
        aria-label="Sponsored coins earned today (UTC)"
        aria-valuemin={0}
        aria-valuemax={progress.cap}
        aria-valuenow={progress.granted}
        aria-valuetext={progress.text}
      >
        <span className="meter-fill meter-coin" style={{ width: `${progress.percent}%` }} />
      </div>
      {progress.reservedText ? <div className="small muted">{progress.reservedText}</div> : null}
    </div>
  );
}

export interface OfferCardActions {
  onPrimary(row: OfferRow): void;
  /** ATTEMPT_IN_PROGRESS: show the resident's own active task. */
  onShowActive?(): void;
  /** "Go there" (in town: leave a building first and frame the store). */
  onGoThere?(row: OfferRow): void;
  /** Why "Go there" is unavailable right now (null = available). */
  goThereReason?: string | null;
  /** Plain pages: link to the place in the town instead of a button. */
  goThereHref?: (row: OfferRow) => string;
}

export function OfferCard({ row, actions, showPlace = false }: { row: OfferRow; actions: OfferCardActions; showPlace?: boolean }) {
  const o = row.offer;
  const c = row.copy;
  return (
    <li className={`offer-card${row.active ? " active" : ""}`} data-testid="offer-card" data-offer-id={o.offerId} data-eligibility={o.eligibility} data-kind={o.kind}>
      <div className="offer-card-head">
        <span className="badge badge-coin offer-reward" data-testid="offer-reward">
          {row.reward}
        </span>
        <span className="badge" data-testid="offer-kind">
          {row.kindLabel}
        </span>
        {o.isHouse ? <span className="badge badge-house">Townsy</span> : <span className="badge badge-sponsored">Sponsored</span>}
        {row.active ? <span className="badge badge-success">In progress</span> : null}
      </div>
      <h3 className="offer-title">{o.title}</h3>
      <div className="small muted">
        {row.sponsorLine}
        {showPlace ? ` · ${o.address}` : ""}
        {o.destinationHost ? ` · ${o.destinationHost}` : ""}
      </div>
      <p className="small offer-instructions">{o.instructions}</p>
      <p className="small muted offer-verification" data-testid="offer-verification">
        <strong>What Townsy checks:</strong> {o.verificationLabel}
      </p>
      {c.message ? (
        <p className="small offer-eligibility" data-testid="offer-eligibility">
          {c.message}
        </p>
      ) : null}
      <div className="row offer-actions">
        <PrimaryAction row={row} actions={actions} />
        {showPlace && actions.onGoThere ? (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => actions.onGoThere?.(row)}
            disabled={Boolean(actions.goThereReason)}
            title={actions.goThereReason ?? `Show ${o.address} in the town`}
            data-testid="offer-go-there"
          >
            Go there
          </button>
        ) : showPlace && actions.goThereHref ? (
          <Link className="btn btn-sm" to={actions.goThereHref(row)} data-testid="offer-go-there">
            Go there
          </Link>
        ) : null}
      </div>
    </li>
  );
}

function PrimaryAction({ row, actions }: { row: OfferRow; actions: OfferCardActions }) {
  const c = row.copy;
  switch (c.cta) {
    case "start":
    case "continue":
      return (
        <button type="button" className="btn btn-sm btn-primary" onClick={() => actions.onPrimary(row)} data-testid="offer-primary">
          {c.label}
        </button>
      );
    case "sign_in":
    case "verify_email":
      return (
        <Link className="btn btn-sm btn-primary" to="/account" data-testid="offer-primary">
          {c.label}
        </Link>
      );
    case "show_active":
      return actions.onShowActive ? (
        <button type="button" className="btn btn-sm" onClick={() => actions.onShowActive?.()} data-testid="offer-primary">
          {c.label}
        </button>
      ) : null;
    default:
      return (
        <button type="button" className="btn btn-sm" disabled title={c.message ?? undefined} data-testid="offer-primary">
          {c.label}
        </button>
      );
  }
}

/** A list of tasks with the disclosure always visible; an empty list says so (no invented rewards). */
export function OfferListView({
  rows,
  disclosure,
  actions,
  showPlace = false,
  progress = null,
  emptyText,
}: {
  rows: readonly OfferRow[];
  disclosure: string;
  actions: OfferCardActions;
  showPlace?: boolean;
  progress?: DailyProgress | null;
  emptyText: string;
}) {
  return (
    <div className="stack offer-list-wrap" style={{ gap: 8 }}>
      <SponsoredDisclosure text={disclosure} compact={!showPlace} />
      {progress ? <DailyProgressMeter progress={progress} /> : null}
      {rows.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }} data-testid="offers-empty">
          {emptyText}
        </p>
      ) : (
        <ul className="offer-list" data-testid="offer-list">
          {rows.map((r) => (
            <OfferCard key={r.key} row={r} actions={actions} showPlace={showPlace} />
          ))}
        </ul>
      )}
    </div>
  );
}
